// ShoeQuestion.tsx
import { useState, useEffect } from "react";
import {useStoreDispatch, useStoreSelector} from "../../store/hooks";
import {enterNumericQuestionAnswer, selectCurrentNumericQuestionAnswers} from "../../store/questionnaireSlice";
import { QuestionConfig } from "../../data/QuestionConfig";
import {Box, Typography, TextField, Slider, useMediaQuery} from "@mui/material";
import { useTranslation } from "react-i18next";
import {theme} from "../../theme.ts";

// EU to US conversion based on the provided table
function convertEuToUs(euSize: number): number {
    const conversions: Record<number, number> = {
        36: 5, 36.5: 5.5, 37: 6, 37.5: 6,
        38: 6.5, 38.5: 6.5, 39: 7, 39.5: 7.5,
        40: 8, 40.5: 8.5, 41: 8.5, 41.5: 9,
        42: 9.5, 42.5: 10, 43: 10.5, 43.5: 10.5,
        44: 11, 44.5: 11.5, 45: 12, 45.5: 12.5,
        46: 12.5, 46.5: 13, 47: 13.5, 47.5: 14,
        48: 14, 48.5: 14.5, 49: 15, 49.5: 15.5,
        50: 16
    };
    return conversions[euSize] ?? 0; // Return 0 if size is not in the map
}

// US to EU conversion based on the provided table
function convertUsToEu(usSize: number): number {
    const conversions: Record<number, number> = {
        5: 36, 5.5: 36.5, 6: 37,
        6.5: 38, 7: 39, 7.5: 39.5, 8: 40,
        8.5: 41, 9: 41.5, 9.5: 42, 10: 42.5,
        10.5: 43, 11: 44, 11.5: 44.5, 12: 45,
        12.5: 46, 13: 46.5, 13.5: 47, 14: 47.5,
        14.5: 48.5, 15: 49, 15.5: 49.5, 16: 50
    };
    return conversions[usSize] ?? 0; // Return 0 if size is not in the map
}

export function ShoeQuestion({ questionConfig }: { questionConfig: QuestionConfig }) {
    const { t } = useTranslation();
    const euConfig = questionConfig.inputConfigs![0];
    const euMin = euConfig.min;
    const euMax = euConfig.max;

    const dispatch = useStoreDispatch();
    const currentAnswers = useStoreSelector(selectCurrentNumericQuestionAnswers);

    // Initialisiere mit gespeichertem Wert oder Default
    const initialEUValue = currentAnswers["shoe_size_eu"] ?? euConfig.defaultValue;
    const initialUSValue = convertEuToUs(initialEUValue);

    const [EUvalue, setEUvalue] = useState<number>(initialEUValue);
    const [euTextFieldValue, setEuTextFieldValue] = useState<string>(initialEUValue.toString());
    const [usTextFieldValue, setUsTextFieldValue] = useState<string>(initialUSValue.toString());

    const [euError, setEuError] = useState(false);
    const [usError, setUsError] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [wasInteracted, setWasInteracted] = useState(false);

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Aktualisiere Feldwerte, wenn Antworten aus dem Store geändert werden
    useEffect(() => {
        const storedEUValue = currentAnswers["shoe_size_eu"];
        if (storedEUValue !== undefined) {
            setEUvalue(storedEUValue);
            setEuTextFieldValue(storedEUValue.toString());
            setUsTextFieldValue(convertEuToUs(storedEUValue).toString());
            setWasInteracted(true);
        }
    }, [currentAnswers]);

    const putValueInState = (value: number) => {
        dispatch(enterNumericQuestionAnswer(["shoe_size_eu", value]));
    };

    const handleSliderChange = (_event: Event, newValue: number | number[]) => {
        if (typeof newValue === 'number') {
            setEUvalue(newValue);
            setEuTextFieldValue(newValue.toString());
            setUsTextFieldValue(convertEuToUs(newValue).toString());
            putValueInState(newValue);
            setWasInteracted(true);
        }
    };

    const handleEuTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setEuTextFieldValue(inputValue);
        setWasInteracted(true);

        if (inputValue === "") {
            setEuError(false);
            return;
        }

        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue)) {
            if (numericValue < euMin || numericValue > euMax) {
                setEuError(true);
            } else {
                setEuError(false);
                setEUvalue(numericValue);
                const usSize = convertEuToUs(numericValue);
                setUsTextFieldValue(usSize.toString());
                putValueInState(numericValue);
            }
        } else {
            setEuError(true);
        }
    };

    const handleUsTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setUsTextFieldValue(inputValue);
        setWasInteracted(true);

        if (inputValue === "") {
            setUsError(false);
            return;
        }

        const numericValue = parseFloat(inputValue);
        if (!isNaN(numericValue)) {
            const euEquivalent = convertUsToEu(numericValue);
            if (euEquivalent === 0 || euEquivalent < euMin || euEquivalent > euMax) {
                setUsError(true);
            } else {
                setUsError(false);
                setEUvalue(euEquivalent);
                setEuTextFieldValue(euEquivalent.toString());
                putValueInState(euEquivalent);
            }
        } else {
            setUsError(true);
        }
    };

    const handleEuBlur = () => {
        setIsFocused(false);
        const numericValue = parseFloat(euTextFieldValue);

        if (euTextFieldValue === "" || isNaN(numericValue)) {
            // Bei leerem Feld setzen wir einen Standardwert
            setEuTextFieldValue(euMin.toString());
            setEUvalue(euMin);
            const usSize = convertEuToUs(euMin);
            setUsTextFieldValue(usSize.toString());
            setEuError(false);
            putValueInState(euMin);
            return;
        }

        if (numericValue < euMin) {
            setEuTextFieldValue(euMin.toString());
            setEUvalue(euMin);
            const usSize = convertEuToUs(euMin);
            setUsTextFieldValue(usSize.toString());
            setEuError(false);
            putValueInState(euMin);
        } else if (numericValue > euMax) {
            setEuTextFieldValue(euMax.toString());
            setEUvalue(euMax);
            const usSize = convertEuToUs(euMax);
            setUsTextFieldValue(usSize.toString());
            setEuError(false);
            putValueInState(euMax);
        }
    };

    const handleUsBlur = () => {
        setIsFocused(false);
        const numericValue = parseFloat(usTextFieldValue);

        if (usTextFieldValue === "" || isNaN(numericValue)) {
            // Bei leerem Feld setzen wir einen Standardwert
            const defaultUsSize = convertEuToUs(euMin);
            setUsTextFieldValue(defaultUsSize.toString());
            setEUvalue(euMin);
            setEuTextFieldValue(euMin.toString());
            setUsError(false);
            putValueInState(euMin);
            return;
        }

        const euEquivalent = convertUsToEu(numericValue);
        if (euEquivalent === 0 || euEquivalent < euMin) {
            const minUsValue = convertEuToUs(euMin);
            setUsTextFieldValue(minUsValue.toString());
            setEUvalue(euMin);
            setEuTextFieldValue(euMin.toString());
            setUsError(false);
            putValueInState(euMin);
        } else if (euEquivalent > euMax) {
            const maxUsValue = convertEuToUs(euMax);
            setUsTextFieldValue(maxUsValue.toString());
            setEUvalue(euMax);
            setEuTextFieldValue(euMax.toString());
            setUsError(false);
            putValueInState(euMax);
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-around",
            my: 3,
            alignItems: "center",
        }}>
            <Box sx={{
                width: "80%",
            }}>
                <Box
                    sx={{
                        display: "flex",
                        gap: isMobile ? "10px" : "20px",
                        justifyContent: 'center',
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "center" : "flex-start",
                        mb: 2
                    }}
                >
                    <Box>
                        <Typography variant="body1">{t('shoe_size_eu')}</Typography>
                        <TextField
                            value={wasInteracted ? euTextFieldValue : ""}
                            type="number"
                            variant="outlined"
                            size="small"
                            inputProps={{
                                min: euMin,
                                max: euMax,
                                step: 0.5
                            }}
                            onChange={handleEuTextFieldChange}
                            onBlur={handleEuBlur}
                            onFocus={handleFocus}
                            error={euError && !isFocused}
                            helperText={euError && !isFocused ? t('shoe_size_value_out_of_range') : " "}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: euError && !isFocused ? "red" : "default",
                                    },
                                },
                                width: "80px", // Set a fixed width for the text field
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body1">{t('shoe_size_us')}</Typography>
                        <TextField
                            value={wasInteracted ? usTextFieldValue : ""}
                            type="number"
                            variant="outlined"
                            size="small"
                            inputProps={{
                                step: 0.5
                            }}
                            onChange={handleUsTextFieldChange}
                            onBlur={handleUsBlur}
                            onFocus={handleFocus}
                            error={usError && !isFocused}
                            helperText={usError && !isFocused ? t('shoe_size_value_out_of_range') : " "}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: usError && !isFocused ? "red" : "default",
                                    },
                                },
                                width: "80px", // Set a fixed width for the text field
                            }}
                        />
                    </Box>
                </Box>
                <Slider
                    value={EUvalue}
                    min={euMin}
                    max={euMax}
                    step={0.5}
                    onChange={handleSliderChange}
                    aria-label={t('shoe_size_slider_label')}
                />
            </Box>
        </Box>
    );
}
